import { useMemo, useState } from "react";
import { Building2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { LinkAcessoDialog, type LinkAcesso } from "@/components/admin/link-acesso-dialog";
import { useCriarFazenda, type NovaFazendaInput } from "@/hooks/use-plataforma";
import { MODULES } from "@/lib/modules";
import { MODULOS_USUARIO } from "@shared/schema";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { Segmented } from "@/components/layout/segmented";
import { AdminLista, AdminListaItem, AdminTabela } from "@/components/admin/admin-lista";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow, DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { StatusBadge, type Tom } from "@/components/data/status-badge";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { useFazendas } from "@/hooks/use-plataforma";
import { filtrarFazendas, type FazendaDTO } from "@shared/plataforma-fazendas";
import { ROTULO_STATUS_ORG, STATUS_ORG, type StatusOrg } from "@shared/org-status";
import { labelOrgTipo } from "@shared/plataforma-organizacoes";

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook) — ver estado-na-url.
const PADRAO = { q: "", tipo: "todos", status: "todos" };
const PERMITIDOS = {
  tipo: ["todos", "PRODUTOR", "COMPRADOR"],
  status: ["todos", ...STATUS_ORG],
} as const;

const COLUNAS = [null, "7rem", "8rem", "11rem", "6rem", "8rem", "6rem"];

/** Tom do badge por status — estado vai no badge, nunca na moldura. */
export const TOM_STATUS: Record<StatusOrg, Tom> = {
  ASSINANTE: "sucesso",
  CORTESIA: "info",
  TRIAL: "atencao",
  TRIAL_EXPIRADO: "erro",
  INATIVA: "neutro",
};

export function BadgeStatusOrg({ f }: { f: Pick<FazendaDTO, "status" | "trialDiasRestantes"> }) {
  const texto = f.status === "TRIAL" && f.trialDiasRestantes !== null
    ? `Trial · ${f.trialDiasRestantes}d`
    : ROTULO_STATUS_ORG[f.status];
  return <StatusBadge tom={TOM_STATUS[f.status]} ponto>{texto}</StatusBadge>;
}

export function fmtData(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/** "Nunca" ou há quantos dias alguém entrou — o que importa é o atraso, não a hora. */
export function fmtUltimoAcesso(iso: string | null | undefined): string {
  if (!iso) return "Nunca";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const dias = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (dias <= 0) return "Hoje";
  if (dias === 1) return "Ontem";
  if (dias < 30) return `Há ${dias} dias`;
  return fmtData(iso);
}

export default function PlataformaFazendas() {
  const { estado, definir } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const { abrir } = useNavegacao();
  const consulta = useFazendas();
  const [criando, setCriando] = useState(false);
  const [linkCriado, setLinkCriado] = useState<{ link: LinkAcesso; nome: string; telefone: string | null } | null>(null);
  const aguardando = estadoDaConsulta(consulta, { variante: "tabela" });

  const filtradas = useMemo(
    () => filtrarFazendas(consulta.data ?? [], { q: estado.q, tipo: estado.tipo, status: estado.status }),
    [consulta.data, estado.q, estado.tipo, estado.status],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Fazendas"
        descricao="Todas as organizações do Rizoma — produtoras e compradoras"
        acao={
          <Button onClick={() => setCriando(true)} className={ALVO_TOQUE_BOTAO} data-testid="button-nova-fazenda">
            <Plus className="w-4 h-4 mr-2" />Nova fazenda
          </Button>
        }
      />

      <PageToolbar
        fim={<PageSearch valor={estado.q} onChange={v => definir({ q: v })} placeholder="Nome, cidade ou slug" testId="input-busca-fazendas" />}
      >
        <Segmented
          valor={estado.tipo}
          onChange={v => definir({ tipo: v })}
          testId="seg-tipo-fazenda"
          opcoes={[
            { id: "todos", label: "Todas" },
            { id: "PRODUTOR", label: "Produtoras" },
            { id: "COMPRADOR", label: "Compradoras" },
          ]}
        />
        <Select value={estado.status} onValueChange={v => definir({ status: v })}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="select-status-fazenda">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            {STATUS_ORG.map(s => (
              <SelectItem key={s} value={s}>{ROTULO_STATUS_ORG[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageToolbar>

      {aguardando ?? (
        filtradas.length === 0 ? (
          estado.q.trim() ? <NadaEncontrado termo={estado.q.trim()} /> : (
            <EmptyState icone={Building2} titulo="Nenhuma fazenda" descricao="Nenhuma organização com este filtro." testId="empty-fazendas" />
          )
        ) : (
          <>
            <AdminLista>
              {filtradas.map(f => (
                <AdminListaItem
                  key={f.id}
                  testId={`card-fazenda-${f.slug}`}
                  onClick={() => abrir(`/plataforma/fazendas/${f.id}`)}
                  atenuado={!f.ativo}
                  identidade={f.nome}
                  meta={
                    <>
                      <Badge variant="outline" className="text-xs font-normal">{labelOrgTipo(f.tipo)}</Badge>
                      <BadgeStatusOrg f={f} />
                      <span>{f.cidade ?? "—"}</span>
                      <span>{f.usuariosAtivos} {f.usuariosAtivos === 1 ? "usuário" : "usuários"}</span>
                      <span>Acesso: {fmtUltimoAcesso(f.ultimoAcesso)}</span>
                    </>
                  }
                />
              ))}
            </AdminLista>

            <AdminTabela>
              <DataTable colunas={COLUNAS}>
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableHead>Fazenda</DataTableHead>
                    <DataTableHead>Tipo</DataTableHead>
                    <DataTableHead>Status</DataTableHead>
                    <DataTableHead>Módulos</DataTableHead>
                    <DataTableHead num>Usuários</DataTableHead>
                    <DataTableHead>Último acesso</DataTableHead>
                    <DataTableHead>Criada</DataTableHead>
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {filtradas.map(f => (
                    <DataTableRow
                      key={f.id}
                      data-testid={`linha-fazenda-${f.slug}`}
                      className={"cursor-pointer " + (!f.ativo ? "opacity-60" : "")}
                      onClick={() => abrir(`/plataforma/fazendas/${f.id}`)}
                    >
                      <DataTableCell destaque className="truncate">
                        <div className="min-w-0">
                          <p className="truncate">{f.nome}</p>
                          <p className="text-xs text-muted-foreground font-normal truncate">
                            {f.cidade ? `${f.cidade} · ` : ""}<span className="font-mono">{f.slug}</span>
                          </p>
                        </div>
                      </DataTableCell>
                      <DataTableCell><Badge variant="outline" className="text-xs font-normal">{labelOrgTipo(f.tipo)}</Badge></DataTableCell>
                      <DataTableCell><BadgeStatusOrg f={f} /></DataTableCell>
                      <DataTableCell className="text-xs text-muted-foreground truncate">{f.modulesEnabled.join(" · ") || "—"}</DataTableCell>
                      <DataTableCell className="text-right tabular-nums">{f.usuariosAtivos}<span className="text-muted-foreground">/{f.usuariosTotal}</span></DataTableCell>
                      <DataTableCell className="text-xs text-muted-foreground">{fmtUltimoAcesso(f.ultimoAcesso)}</DataTableCell>
                      <DataTableCell className="text-xs text-muted-foreground tabular-nums">{fmtData(f.createdAt)}</DataTableCell>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </DataTable>
            </AdminTabela>
          </>
        )
      )}

      {criando && (
        <DialogNovaFazenda
          onFechar={() => setCriando(false)}
          onCriada={(r) => {
            setCriando(false);
            if (r.convite) setLinkCriado({ link: r.convite, nome: r.admin.name, telefone: r.admin.telefone });
          }}
        />
      )}
      {linkCriado && (
        <LinkAcessoDialog
          link={linkCriado.link}
          titulo="Fazenda criada — o acesso do responsável"
          nome={linkCriado.nome}
          telefone={linkCriado.telefone}
          onFechar={() => setLinkCriado(null)}
        />
      )}
    </div>
  );
}

/**
 * Nova fazenda (P2): a org + o ADMIN convidado (sem senha — o link vai por
 * e-mail/WhatsApp), no regime escolhido: trial de 15 dias, cortesia permanente
 * ou cortesia até uma data.
 */
function DialogNovaFazenda({ onFechar, onCriada }: { onFechar: () => void; onCriada: (r: { admin: { name: string; telefone: string | null }; convite: LinkAcesso | null }) => void }) {
  const { toast } = useToast();
  const criar = useCriarFazenda();
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState<"PRODUTOR" | "COMPRADOR">("PRODUTOR");
  const [cidade, setCidade] = useState("");
  const [praca, setPraca] = useState("");
  const [respNome, setRespNome] = useState("");
  const [respEmail, setRespEmail] = useState("");
  const [respTel, setRespTel] = useState("");
  const [modulos, setModulos] = useState<string[]>(["cacho", "equipe"]);
  const [regime, setRegime] = useState<NovaFazendaInput["regime"]>("TRIAL_15");
  const [ate, setAte] = useState("");

  function mudarTipo(t: "PRODUTOR" | "COMPRADOR") {
    setTipo(t);
    setModulos(t === "COMPRADOR" ? ["mercado"] : ["cacho", "equipe"]);
  }
  const alternar = (id: string) => setModulos(prev => (prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]));

  const invalido = nome.trim().length < 2 || respNome.trim().length < 2 || (!respEmail.trim() && !respTel.trim()) || (regime === "CORTESIA_ATE" && !ate);

  function salvar() {
    criar.mutate({
      nomeFazenda: nome.trim(), tipo, cidade: cidade.trim() || null, praca: praca.trim() || null,
      responsavel: { nome: respNome.trim(), email: respEmail.trim() || null, telefone: respTel.trim() || null },
      modulesEnabled: modulos, regime, cortesiaAte: regime === "CORTESIA_ATE" && ate ? new Date(ate + "T23:59:59").toISOString() : null,
    }, {
      onSuccess: r => { toast({ title: "Fazenda criada", description: r.org.nome }); onCriada(r); },
      onError: (e: Error) => toast({ title: "Não deu para criar", description: e.message, variant: "destructive" }),
    });
  }

  return (
    <Dialog open onOpenChange={a => { if (!a) onFechar(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-nova-fazenda">
        <DialogHeader>
          <DialogTitle>Nova fazenda</DialogTitle>
          <DialogDescription>A organização e o acesso do responsável, que recebe um link para definir a senha.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="nf-nome">Nome da fazenda / organização</Label>
            <Input id="nf-nome" value={nome} onChange={e => setNome(e.target.value)} data-testid="input-nf-nome" />
          </div>
          <div className="space-y-1">
            <Label>Tipo</Label>
            <div className="flex gap-2">
              {(["PRODUTOR", "COMPRADOR"] as const).map(t => (
                <Button key={t} type="button" size="sm" variant={tipo === t ? "default" : "outline"} onClick={() => mudarTipo(t)} data-testid={`button-nf-tipo-${t}`}>
                  {t === "PRODUTOR" ? "Produtor" : "Comprador"}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="nf-cidade">Cidade</Label>
            <Input id="nf-cidade" value={cidade} onChange={e => setCidade(e.target.value)} data-testid="input-nf-cidade" />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="nf-praca">Praça padrão (slug, opcional)</Label>
            <Input id="nf-praca" value={praca} onChange={e => setPraca(e.target.value)} placeholder="irece" data-testid="input-nf-praca" />
          </div>
          <div className="md:col-span-2 border-t pt-3">
            <p className="text-sm font-medium mb-2">Responsável (será o Admin)</p>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1"><Label htmlFor="nf-resp">Nome</Label><Input id="nf-resp" value={respNome} onChange={e => setRespNome(e.target.value)} data-testid="input-nf-resp-nome" /></div>
              <div className="space-y-1"><Label htmlFor="nf-email">E-mail</Label><Input id="nf-email" type="email" value={respEmail} onChange={e => setRespEmail(e.target.value)} data-testid="input-nf-resp-email" /></div>
              <div className="space-y-1"><Label htmlFor="nf-tel">Telefone</Label><Input id="nf-tel" type="tel" value={respTel} onChange={e => setRespTel(e.target.value)} placeholder="(74) 99999-8888" data-testid="input-nf-resp-tel" /></div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">E-mail ou telefone — pelo menos um. O link de acesso vai para o e-mail, se houver; senão você manda pelo WhatsApp.</p>
          </div>
          <div className="md:col-span-2 border-t pt-3">
            <p className="text-sm font-medium mb-2">Módulos</p>
            <div className="flex flex-wrap gap-3">
              {MODULOS_USUARIO.map(id => (
                <label key={id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={modulos.includes(id)} onCheckedChange={() => alternar(id)} data-testid={`checkbox-nf-modulo-${id}`} />
                  {MODULES.find(m => m.id === id)?.label ?? id}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 border-t pt-3 space-y-2">
            <p className="text-sm font-medium">Regime</p>
            {([
              ["TRIAL_15", "Trial de 15 dias"],
              ["CORTESIA", "Cortesia permanente"],
              ["CORTESIA_ATE", "Cortesia até uma data"],
            ] as const).map(([id, rotulo]) => (
              <label key={id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="regime" checked={regime === id} onChange={() => setRegime(id)} data-testid={`radio-nf-regime-${id}`} />
                {rotulo}
              </label>
            ))}
            {regime === "CORTESIA_ATE" && (
              <Input type="date" value={ate} onChange={e => setAte(e.target.value)} className="max-w-[200px]" data-testid="input-nf-cortesia-ate" />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button onClick={salvar} disabled={invalido || criar.isPending} data-testid="button-nf-salvar">{criar.isPending ? "Criando…" : "Criar fazenda"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
